import { createSignal, For, Show, type Component } from "solid-js";
import type { FilterBuilder, FilterOperator } from "./filterBuilder";
import { useWire } from "~/wire";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { LabelPicker } from "../labels";
import { TbFilter, TbPlus } from "solid-icons/tb";
import { twMerge } from "tailwind-merge";
import { LabelClosableButton } from "../labels/LabelClosableButton";
import type { Asset, AssetId, Concept, ConceptId, LabelId } from "@storytell/types";

export const AssetsFilterView: Component<{
  mount?: Node;
  builder: FilterBuilder<Asset | Concept>;
}> = (props) => {
  const wire = useWire();
  const [search, setSearch] = createSignal("");
  const getLabel = wire.sync.labels.get;

  const filterLabelsBySearch = () => wire.sync.labels.search(search());

  const addLabelFilter = (labelId: string, groupIndex: number) => {
    props.builder.addFilter(
      {
        operator: "and",
        filter: (entity) =>
          entity.id.startsWith("asset_")
            ? wire.sync.getLabelsForAsset(entity.id as AssetId).some((id) => id === labelId)
            : wire.sync.getLabelsForConcept(entity.id as ConceptId).some((id) => id === labelId),
        id: labelId as LabelId,
        kind: "label",
      },
      groupIndex,
    );
  };

  const removeLabelFilter = (labelId: string, groupIndex: number) => {
    props.builder.removeFilter(labelId, groupIndex);
  };

  return (
    <div class="flex flex-col gap-2">
      <For each={props.builder.state.filter}>
        {(group, groupIndex) => (
          <div class="flex flex-col gap-2 p-2 rounded-lg border border-muted-border dark:border-primary-border">
            <Show when={groupIndex() > 0}>
              <div class="flex items-center gap-2">
                <FilterOperatorSwitcher
                  variant="blue"
                  operator={group.operator}
                  setOperator={(op) => {
                    props.builder.changeGroupOperator(op, groupIndex());
                  }}
                />
                <span class="text-xs text-secondary-text">this filter group</span>
              </div>
            </Show>

            <div class="flex items-center gap-3">
              <div class="flex items-center gap-3 flex-wrap">
                <For each={group.filters}>
                  {(filter, filterIndex) => (
                    <>
                      <Show when={filterIndex() > 0}>
                        <FilterOperatorSwitcher
                          variant="violet"
                          operator={filter.operator}
                          setOperator={(op) => {
                            props.builder.changeFilterOperator(filter.id, op, groupIndex());
                          }}
                        />
                      </Show>
                      <LabelClosableButton
                        class="px-2 py-1.5 gap-2"
                        label={getLabel(filter.id as LabelId)!}
                        onClose={() => removeLabelFilter(filter.id, groupIndex())}
                        onClick={() => {}}
                      />
                    </>
                  )}
                </For>

                <StPopover
                  contentProps={{ class: "z-50" }}
                  portalProps={{
                    mount: props.mount,
                  }}
                  placement="bottom-start"
                  noArrow
                  gutter={10}
                  content={
                    <LabelPicker
                      disableCreate
                      mount={props.mount}
                      search={search()}
                      setSearch={setSearch}
                      labels={filterLabelsBySearch()}
                      selectedLabels={group.filters.map((f) => f.id)}
                      onSelectLabel={(l) => addLabelFilter(l.id, groupIndex())}
                      onDeselectLabel={(l) => removeLabelFilter(l.id, groupIndex())}
                      onCreateLabel={async () => {}}
                    />
                  }
                >
                  <StPopoverTrigger
                    as={StButton}
                    iconClass="size-3"
                    icon={TbFilter}
                    noColor
                    class="dark:border-primary-border"
                  >
                    Add Filter
                  </StPopoverTrigger>
                </StPopover>
              </div>

              <Show when={props.builder.state.filter.length > 1 || group.filters.length > 0}>
                <button
                  type="button"
                  class="text-xs underline text-secondary-text hover:text-primary-text cursor-pointer underline-offset-2"
                  onClick={() => {
                    if (props.builder.state.filter.length === 1) {
                      props.builder.resetFilter();
                      return;
                    }
                    props.builder.removeFilterGroup(groupIndex());
                  }}
                >
                  Clear
                </button>
              </Show>
            </div>
          </div>
        )}
      </For>

      <Show when={(props.builder.state.filter.at(-1)?.filters.length ?? 0) > 0}>
        <div class="">
          <StButton icon={TbPlus} noColor noBorder onClick={() => props.builder.addFilterGroup()}>
            Add Filter Group
          </StButton>
        </div>
      </Show>
    </div>
  );
};

const FilterOperatorSwitcher = (props: {
  operator: FilterOperator;
  setOperator: (operator: FilterOperator) => void;
  variant: "violet" | "blue";
}) => {
  return (
    <StButton
      noBorder
      noColor
      class={twMerge(
        "p-1 text-on-brand uppercase text-[0.5rem] tracking-widest w-8",
        props.variant === "violet" && "bg-violet-600 hover:bg-violet-500",
        props.variant === "blue" && "bg-blue-600 hover:bg-blue-500",
      )}
      onClick={() => props.setOperator(props.operator === "and" ? "or" : "and")}
    >
      {props.operator}
    </StButton>
  );
};
